<template>
  <div class="search-badge-wrapper" ref="wrapper">
    <div 
      class="search-badge" 
      :class="{ 
        'search-badge-expanded': isExpanded || modelValue || hasSelection,
        'search-badge-dropdown': mode === 'dropdown'
      }"
      @click="toggleSearch"
    >
      <button class="search-icon-btn" @click.stop="toggleSearch">
        <i :class="icon"></i>
      </button>
      
      <!-- Dropdown mode: show selected item or search input -->
      <template v-if="mode === 'dropdown'">
        <input
          ref="searchInput"
          type="text"
          class="search-badge-input"
          :class="{ 'search-badge-input-collapsed': !isExpanded }"
          :placeholder="isExpanded ? placeholder : (hasSelection ? selectedLabel : label)"
          :value="isExpanded ? searchQuery : ''"
          :readonly="!isExpanded"
          @input="searchQuery = $event.target.value; onSearchInput()"
          @keyup.esc="closeSearch"
          @click.stop="toggleSearch"
        />
      </template>
      
      <!-- Default search mode -->
      <template v-else>
        <input
          ref="searchInput"
          type="text"
          class="search-badge-input"
          :class="{ 'search-badge-input-collapsed': !isExpanded && !modelValue }"
          :placeholder="isExpanded || modelValue ? placeholder : label"
          :value="modelValue"
          :readonly="!isExpanded && !modelValue"
          @input="$emit('update:modelValue', $event.target.value)"
          @blur="handleBlur"
          @keyup.esc="closeSearch"
          @click.stop="!isExpanded && !modelValue ? toggleSearch() : null"
        />
      </template>
      
      <!-- Clear/Close button -->
      <button 
        v-if="modelValue || hasSelection" 
        class="clear-btn" 
        @click.stop="closeSearch"
        type="button"
      >
        <i class="fas fa-times"></i>
      </button>
      
      <!-- Dropdown arrow for dropdown mode -->
      <button 
        v-else-if="mode === 'dropdown' && !isExpanded" 
        class="dropdown-arrow-btn"
        @click.stop="toggleSearch"
        type="button"
      >
        <i class="fas fa-chevron-down"></i>
      </button>
    </div>
    
    <!-- Dropdown options list -->
    <div 
      v-if="mode === 'dropdown' && isExpanded" 
      class="search-dropdown-menu"
      ref="dropdownMenu"
      @scroll="handleScroll"
    >
      <div v-if="loading && filteredOptions.length === 0" class="search-dropdown-loading">
        <i class="fas fa-spinner fa-spin me-2"></i>
        Loading...
      </div>
      <template v-else>
        <div 
          v-if="filteredOptions.length === 0" 
          class="search-dropdown-empty"
        >
          No results found
        </div>
        <div 
          v-for="option in filteredOptions" 
          :key="option.value"
          class="search-dropdown-item"
          :class="{ 'selected': isSelected(option) }"
          @click.stop="selectOption(option)"
        >
          <span class="option-label">{{ option.label }}</span>
          <i v-if="isSelected(option)" class="fas fa-check option-check"></i>
        </div>
        <!-- Loading more indicator -->
        <div v-if="loading && filteredOptions.length > 0" class="search-dropdown-loading-more">
          <i class="fas fa-spinner fa-spin me-2"></i>
          Loading more...
        </div>
        <div v-else-if="hasMore" class="search-dropdown-load-more" @click.stop="$emit('load-more')">
          <span>Load more...</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchBar',
  props: {
    modelValue: {
      type: [String, Number, Array, Object],
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Search...'
    },
    label: {
      type: String,
      default: 'Search'
    },
    icon: {
      type: String,
      default: 'fas fa-search'
    },
    mode: {
      type: String,
      default: 'search', // 'search' or 'dropdown'
      validator: (value) => ['search', 'dropdown'].includes(value)
    },
    options: {
      type: Array,
      default: () => []
      // Expected format: [{ value: 1, label: 'Option 1' }, ...]
    },
    loading: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'search', 'select', 'load-more'],
  data() {
    return {
      isExpanded: false,
      searchQuery: ''
    };
  },
  computed: {
    hasSelection() {
      if (this.mode !== 'dropdown') return false;
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.length > 0;
      }
      return this.modelValue !== null && this.modelValue !== undefined && this.modelValue !== '';
    },
    selectedLabel() {
      if (!this.hasSelection) return '';

      if (this.multiple && Array.isArray(this.modelValue)) {
        if (this.modelValue.length === 1) {
          const option = this.options.find(o => o.value === this.modelValue[0]);
          return option?.label || this.modelValue[0];
        }
        return `${this.modelValue.length} selected`;
      }
      const option = this.options.find(o => o.value === this.modelValue);
      return option?.label || this.modelValue;
    },
    filteredOptions() {
      if (!this.searchQuery) return this.options;
      const query = this.searchQuery.toLowerCase();
      return this.options.filter(option => 
        option.label.toLowerCase().includes(query)
      );
    }
  },
  methods: {
    toggleSearch() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.searchQuery = '';
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    handleBlur() {
      if (this.mode === 'search' && !this.modelValue) {
        setTimeout(() => {
          this.isExpanded = false;
        }, 200);
      }
    },
    closeSearch() {
      if (this.mode === 'dropdown') {
        this.$emit('update:modelValue', this.multiple ? [] : null);
        this.$emit('select', this.multiple ? [] : null);
      } else {
        this.$emit('update:modelValue', '');
      }
      this.searchQuery = '';
      this.isExpanded = false;
    },
    onSearchInput() {
      this.$emit('search', this.searchQuery);
    },
    selectOption(option) {
      if (this.multiple) {
        const currentValue = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
        const index = currentValue.indexOf(option.value);
        
        if (index > -1) {
          currentValue.splice(index, 1);
        } else {
          currentValue.push(option.value);
        }

        this.$emit('update:modelValue', currentValue);
        this.$emit('select', currentValue);
      } else {
        this.$emit('update:modelValue', option.value);
        this.$emit('select', option);
        this.isExpanded = false;
      }
      this.searchQuery = '';
    },
    isSelected(option) {
      if (this.multiple && Array.isArray(this.modelValue)) {
        return this.modelValue.includes(option.value);
      }
      return this.modelValue === option.value;
    },
    handleClickOutside(event) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(event.target)) {
        this.isExpanded = false;
      }
    },
    handleScroll(event) {
      const element = event.target;
      const scrollThreshold = 50; // pixels from bottom to trigger load more
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < scrollThreshold;
      
      if (isNearBottom && this.hasMore && !this.loading) {
        this.$emit('load-more');
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.search-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.search-badge {
  background: #f8f9fa;
  border: 1.5px solid #dee1e5;
  border-radius: 1rem;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.search-badge-expanded {
  min-width: 250px;
  background: white;
}

.search-badge-dropdown {
  /* no min-width when collapsed */
}

.search-badge-dropdown.search-badge-expanded {
  min-width: 220px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
}

.search-badge-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.85rem;
  color: #495057;
  flex: 1;
  cursor: text;
  width: 100%;
}

.search-badge-input-collapsed {
  cursor: pointer;
  caret-color: transparent;
  width: auto;
  flex: 0 0 auto;
}

.search-badge-input-collapsed::placeholder {
  color: #6c757d;
  font-weight: 600;
}

.search-badge-input::placeholder {
  color: #adb5bd;
}

.search-icon-btn {
  background: transparent;
  border: none;
  color: #6c757d;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  margin-right: 0.25rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
}

.search-icon-btn:hover {
  color: #495057;
}

.clear-btn {
  background: transparent;
  border: none;
  color: #6c757d;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  margin-left: 0.30rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
}

.clear-btn:hover {
  color: #dc3545;
}

.dropdown-arrow-btn {
  background: transparent;
  border: none;
  color: #6c757d;
  font-size: 0.65rem;
  cursor: pointer;
  padding: 0;
  margin-left: 0.25rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.dropdown-arrow-btn:hover {
  color: #495057;
}

/* Dropdown menu styles */
.search-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1.5px solid #dee1e5;
  border-top: none;
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 220px;
  overflow-y: auto;
  z-index: 1050;
  animation: dropdownFadeIn 0.15s ease-out;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-dropdown-item {
  padding: 0.6rem 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #495057;
  transition: all 0.15s ease;
}

.search-dropdown-item:hover {
  background: rgba(var(--primary-color-rgb, 13, 110, 253), 0.08);
}

.search-dropdown-item.selected {
  background: rgba(var(--primary-color-rgb, 13, 110, 253), 0.1);
  color: var(--primary-color, #0d6efd);
  font-weight: 500;
}

.search-dropdown-item:last-child {
  border-bottom-left-radius: calc(1rem - 2px);
  border-bottom-right-radius: calc(1rem - 2px);
}

.option-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.option-check {
  font-size: 0.75rem;
  color: var(--primary-color, #0d6efd);
  margin-left: 0.5rem;
}

.search-dropdown-loading,
.search-dropdown-empty {
  padding: 1rem;
  text-align: center;
  color: #6c757d;
  font-size: 0.85rem;
}

.search-dropdown-loading-more {
  padding: 0.75rem;
  text-align: center;
  color: #6c757d;
  font-size: 0.8rem;
  border-top: 1px solid #e9ecef;
}

.search-dropdown-load-more {
  padding: 0.75rem;
  text-align: center;
  color: var(--primary-color, #0d6efd);
  font-size: 0.8rem;
  cursor: pointer;
  border-top: 1px solid #e9ecef;
  transition: background 0.2s ease;
}

.search-dropdown-load-more:hover {
  background: rgba(var(--primary-color-rgb, 13, 110, 253), 0.05);
}

/* Scrollbar styling for dropdown */
.search-dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.search-dropdown-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.search-dropdown-menu::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.search-dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

@media (max-width: 768px) {
  .search-badge-expanded {
    min-width: 180px;
  }
  
  .search-badge-dropdown.search-badge-expanded {
    min-width: 180px;
  }
}
</style>
